import { differenceInDays } from 'date-fns';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const FIRST_THIRTY_DAYS_THRESHOLD = 30;

/**
 * Hook for consumers to check if the connector onboarding experiment is enabled.
 * Use this to conditionally render a ConnectorPromo* entry point component.
 *
 * Eligibility criteria:
 * - Rovo must be enabled
 * - User must have admin access
 * - User must be a founding admin (exclude nth users)
 * - User must be within the first 30 days of their enrolment date
 *
 * @param numberOfDays - Optional number of days since enrolment to consider the user eligible.
 *   Defaults to `FIRST_THIRTY_DAYS_THRESHOLD` (30 days).
 *
 * @example
 * ```tsx
 * // Default (30 days)
 * const { isEnabled } = useIsConnectorOnboardingEnabled();
 *
 * // Custom threshold
 * const { isEnabled } = useIsConnectorOnboardingEnabled(60);
 *
 * {isEnabled && <ConnectorPromoNavSearch />}
 * ```
 */
export function useIsConnectorOnboardingEnabled(
	numberOfDays: number = FIRST_THIRTY_DAYS_THRESHOLD,
): { isEnabled: boolean } {
	const { isRovoEnabled } = useSessionData();
	const isAdmin = useIsAdmin();

	const shouldSkipForPreConditions = !isRovoEnabled || !isAdmin;

	// Check if the user is the founding admin (exclude nth users) and fetch enrolment date
	const { data: onboardingStateData, loading: isOnboardingStateLoading } = useGetOnboardingState(
		Object.values(TWCG_1489_CONNECTOR_ONBOARDING),
		!fg('twcg-1489-cc-3p-connector-onboarding-fg') || shouldSkipForPreConditions,
	);
	const { enrolmentDate, promoDismissed } = deserializeState(onboardingStateData);

	if (shouldSkipForPreConditions || isOnboardingStateLoading) {
		return { isEnabled: false };
	}

	// Skip when the user has dismissed both promo surfaces
	if (promoDismissed === 'both') {
		return { isEnabled: false };
	}

	// Only show to users within the first 30 days of their experiment enrolment
	const isWithinFirstThirtyDays =
		Boolean(enrolmentDate) &&
		differenceInDays(new Date(), new Date(enrolmentDate as string)) < numberOfDays;

	if (!isWithinFirstThirtyDays) {
		return { isEnabled: false };
	}

	return { isEnabled: expVal('twcg-1489-cc-3p-connector-onboarding', 'isEnabled', false) };
}
